import React, { useState } from 'react';
import { Navigate, Switch } from '@common/components';
import { PrivateRoute } from '@routes/private-route';
import {
  SUPPLIER_ADMIN_MENU,
  SupplierAdminVerticalNavMenu,
} from 'works/pages/supplier/admin-settings/supplier-admin-vertical-nav-menu/supplier-admin-vertical-nav-menu';
import {
  AdminMembersPage,
  MembersAddEditSteps,
  MembersAddMultipleSteps,
} from '@identity/pages';
import { NoMatch } from '@routes/no-match';
import {
  AdminSupplierDetailsPage,
  OrganizationInvitation,
} from '@institutions/pages/supplier';
import { useCurrentMembership } from '@identity/hooks';

const permissionKeys = ['canShowAdminMembers'] as const;

type PermissionKey = (typeof permissionKeys)[number];

type PermissionsSet = Record<PermissionKey, boolean>;

export function SupplierAdminSettingsRoutes() {
  const [shouldShowMenu, setShouldShowMenu] = useState(true);
  const currentMembership = useCurrentMembership();

  const permissions = permissionKeys.reduce((acc, key) => {
    acc[key] = currentMembership[key] ?? false;
    return acc;
  }, {} as PermissionsSet);

  return (
    <SupplierAdminVerticalNavMenu
      permissions={permissions}
      hideMenu={!shouldShowMenu}
    >
      <Switch>
        <PrivateRoute
          exact
          path="/works/supplier/admin"
          menuItem="redirect"
          component={() => <Navigate to={SUPPLIER_ADMIN_MENU.DETAILS.ROUTE} />}
        />
        <PrivateRoute
          path={SUPPLIER_ADMIN_MENU.DETAILS.ROUTE}
          component={AdminSupplierDetailsPage}
          menuItem="admin"
          onRender={() => setShouldShowMenu(true)}
        />
        <PrivateRoute
          menuItem="admin"
          component={MembersAddEditSteps}
          path={`${SUPPLIER_ADMIN_MENU.USERS.ROUTE}/add`}
          secondaryMenuItem="members-page"
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminMembers}
        />
        <PrivateRoute
          menuItem="admin"
          component={MembersAddEditSteps}
          path={`${SUPPLIER_ADMIN_MENU.USERS.ROUTE}/edit/:email`}
          secondaryMenuItem="members-page"
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminMembers}
        />
        <PrivateRoute
          menuItem="admin"
          component={MembersAddMultipleSteps}
          path={`${SUPPLIER_ADMIN_MENU.USERS.ROUTE}/upload`}
          secondaryMenuItem="members-page"
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminMembers}
        />
        <PrivateRoute
          menuItem="admin"
          component={AdminMembersPage}
          path={SUPPLIER_ADMIN_MENU.USERS.ROUTE}
          secondaryMenuItem="members-page"
          onRender={() => setShouldShowMenu(true)}
          permitted={permissions.canShowAdminMembers}
        />
        <PrivateRoute
          path="/works/supplier/admin/organization-invitations/invitation/:invitationToken"
          component={OrganizationInvitation}
          menuItem="admin"
        />
        <PrivateRoute component={NoMatch} menuItem={null} />
      </Switch>
    </SupplierAdminVerticalNavMenu>
  );
}
